import { Hotel, RoomClass } from 'ts/hotel/interfaces/HotelResponse';
import { getCenteredHotel, getOriginalHotelKey } from 'utils/hotel';
import { FocusedMarker, Position } from 'ts/hotel/interfaces/Generic';
import { publishLocalRoomFilterAction } from 'utils/assistantChatStore';

export const HOTEL_CARD_ACTION = 'hotel-card-action';

export const HOTEL_CARD_ACTIONS = [
  'show-rooms',
  'pin',
  'center-me',
  'www',
  'focus',
  'room-offers',
  'add-to-proposal',
  'open-preferred',
  'open-refund',
  'click-price',
  'filter-rooms',
] as const;

export type HotelCardActionKind = (typeof HOTEL_CARD_ACTIONS)[number];

export interface HotelCardActionPayload {
  action: HotelCardActionKind;
  hotelName?: string | null;
  hotelKey?: string | null;
  index?: number | null;
  roomName?: string | null;
  roomKey?: string | null;
  pinned?: boolean | null;
  show?: boolean | null;
  roomFilterName?: string | null;
  roomRefundableOnly?: boolean | null;
  roomAccessibleOnly?: boolean | null;
  roomBoardingOnly?: boolean | null;
  roomMealPlans?: string[] | null;
  roomSortByCxl?: boolean | null;
  clearRoomFilters?: boolean | null;
}

const ACTION_ALIASES: Record<string, HotelCardActionKind> = {
  proposal: 'add-to-proposal',
  'add-proposal': 'add-to-proposal',
  envelope: 'add-to-proposal',
  ref: 'open-refund',
  refund: 'open-refund',
  'no-ref': 'open-refund',
  cancellation: 'open-refund',
  'cxl-date': 'open-refund',
  price: 'click-price',
  'room-price': 'click-price',
  filter: 'filter-rooms',
  'room-filter': 'filter-rooms',
  'filter-room': 'filter-rooms',
  'pin-hotel': 'pin',
  unpin: 'pin',
  'focus-hotel': 'focus',
  'focus-on-map': 'focus',
};

export const cardActionNeedsDom = (action: HotelCardActionKind) =>
  action === 'show-rooms' ||
  action === 'room-offers' ||
  action === 'open-preferred' ||
  action === 'open-refund' ||
  action === 'click-price' ||
  action === 'filter-rooms';

export const isHotelCardAction = (
  action: string | null | undefined,
): action is HotelCardActionKind => {
  if (!action) {
    return false;
  }
  const normalized = ACTION_ALIASES[action] || action;
  return (HOTEL_CARD_ACTIONS as readonly string[]).includes(normalized);
};

export const parseHotelCardActionPayload = (
  raw: Record<string, unknown> | null | undefined,
): HotelCardActionPayload | null => {
  if (!raw || typeof raw.action !== 'string') {
    return null;
  }

  const action = (ACTION_ALIASES[raw.action] || raw.action) as HotelCardActionKind;
  if (!isHotelCardAction(action)) {
    return null;
  }

  const mealPlans = Array.isArray(raw.roomMealPlans)
    ? raw.roomMealPlans
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim().toUpperCase())
        .filter((item) => ['BB', 'HB', 'FB', 'AI'].includes(item))
    : null;

  const index =
    typeof raw.index === 'number' && Number.isFinite(raw.index) ? Math.floor(raw.index) : null;

  return {
    action,
    hotelName: typeof raw.hotelName === 'string' ? raw.hotelName : null,
    hotelKey: typeof raw.hotelKey === 'string' ? raw.hotelKey : null,
    index: index && index >= 1 ? index : null,
    roomName: typeof raw.roomName === 'string' ? raw.roomName : null,
    roomKey: typeof raw.roomKey === 'string' ? raw.roomKey : null,
    pinned: typeof raw.pinned === 'boolean' ? raw.pinned : null,
    show: typeof raw.show === 'boolean' ? raw.show : null,
    roomFilterName: typeof raw.roomFilterName === 'string' ? raw.roomFilterName : null,
    roomRefundableOnly: typeof raw.roomRefundableOnly === 'boolean' ? raw.roomRefundableOnly : null,
    roomAccessibleOnly: typeof raw.roomAccessibleOnly === 'boolean' ? raw.roomAccessibleOnly : null,
    roomBoardingOnly: typeof raw.roomBoardingOnly === 'boolean' ? raw.roomBoardingOnly : null,
    roomMealPlans: mealPlans,
    roomSortByCxl: typeof raw.roomSortByCxl === 'boolean' ? raw.roomSortByCxl : null,
    clearRoomFilters: typeof raw.clearRoomFilters === 'boolean' ? raw.clearRoomFilters : null,
  };
};

const hotelHasOpenableRooms = (hotel: Hotel) =>
  (hotel.roomClasses || []).some((room) => room.visible !== false);

export const orderHotelsForCardIndex = (hotels: Hotel[], pinnedHotelsIds: string[] = []) => {
  const pinnedSet = new Set(pinnedHotelsIds);
  return hotels
    .filter((hotel) => hotel.visible !== false)
    .sort((a, b) => {
      const aPinned = pinnedSet.has(getOriginalHotelKey(a.htuKey)) ? 0 : 1;
      const bPinned = pinnedSet.has(getOriginalHotelKey(b.htuKey)) ? 0 : 1;
      return aPinned - bPinned;
    });
};

export const findHotelForCardAction = (
  hotels: Hotel[],
  payload: HotelCardActionPayload,
  activeHotel?: Hotel | null,
  pinnedHotelsIds: string[] = [],
): Hotel | undefined => {
  if (payload.index && payload.index >= 1) {
    return orderHotelsForCardIndex(hotels, pinnedHotelsIds)[payload.index - 1];
  }

  if (payload.hotelKey) {
    const key = payload.hotelKey;
    const byKey =
      hotels.find((hotel) => hotel.htuKey === key) ||
      hotels.find(
        (hotel) => getOriginalHotelKey(hotel.htuKey) === getOriginalHotelKey(key),
      );
    if (byKey) {
      return byKey;
    }
  }

  const name = payload.hotelName?.trim().toLowerCase();
  if (name) {
    const exact = hotels.filter((hotel) => hotel.item.name.toLowerCase() === name);
    const matches = exact.length
      ? exact
      : hotels.filter((hotel) => hotel.item.name.toLowerCase().includes(name));

    if (!matches.length) {
      return undefined;
    }

    const withRooms = matches.filter(hotelHasOpenableRooms);
    const pool = withRooms.length ? withRooms : matches;

    return pool.find((hotel) => hotel.visible) || pool[0];
  }

  if (payload.hotelKey) {
    return undefined;
  }

  if (!activeHotel) {
    return undefined;
  }

  return (
    hotels.find((hotel) => hotel.htuKey === activeHotel.htuKey) ||
    hotels.find(
      (hotel) =>
        getOriginalHotelKey(hotel.htuKey) === getOriginalHotelKey(activeHotel.htuKey),
    ) ||
    activeHotel
  );
};

export const bringHotelToFront = (hotels: Hotel[], hotel: Hotel): Hotel[] | null => {
  if (!hotel.visible || !hotelHasOpenableRooms(hotel)) {
    return null;
  }

  const index = hotels.findIndex((item) => item.htuKey === hotel.htuKey);
  if (index <= 0) {
    return null;
  }

  const next = hotels.slice();
  const [item] = next.splice(index, 1);
  next.unshift(item);
  return next;
};

export const revealHotelForCardAction = (hotels: Hotel[], hotel: Hotel): Hotel[] => {
  const index = hotels.findIndex((item) => item.htuKey === hotel.htuKey);
  if (index === -1) {
    return hotels;
  }

  const next = hotels.slice();
  const [item] = next.splice(index, 1);
  const roomClasses = (item.roomClasses || []).map((room) =>
    room.visible === false
      ? { ...room, visible: true, visibleSm: true, visibleFiltered: true }
      : room,
  );
  next.unshift({ ...item, visible: true, roomClasses });
  return next;
};

export const scrollHotelCardIntoView = (htuKey: string) => {
  const card = document.getElementById(htuKey);
  const container = document.querySelector('.hotel-list');
  if (container && card) {
    const containerRect = container.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const offset = cardRect.top - containerRect.top - 100;
    if (Math.abs(offset) > 1) {
      container.scrollBy({ top: offset, behavior: 'smooth' });
    }
    return;
  }
  card?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
};

export const findRoomForCardAction = (
  hotel: Hotel,
  payload: HotelCardActionPayload,
): RoomClass | undefined => {
  const rooms = hotel.roomClasses || [];
  if (payload.roomKey) {
    const byKey = rooms.find((room) => room.rcUniqueKey === payload.roomKey);
    if (byKey) {
      return byKey;
    }
  }

  const name = payload.roomName?.trim().toLowerCase();
  if (!name) {
    return rooms[0];
  }

  return (
    rooms.find((room) =>
      room.hotelRooms?.some((hotelRoom) =>
        (hotelRoom.displayName || '').toLowerCase().includes(name),
      ),
    ) || rooms[0]
  );
};

export interface HotelCardActionHandlers {
  setActiveHotel: (hotel?: Hotel) => void;
  setPinnedHotelsIds: (ids: string[] | ((prev: string[]) => string[])) => void;
  pinnedHotelsIds: string[];
  setCentered: (pos: Position | undefined) => void;
  setFocusedHotel: (marker: FocusedMarker) => void;
  setActiveTab: (tab: number) => void;
  setAreActiveRooms?: (value: boolean) => void;
  showRoomsInTab?: boolean;
  displayStyle?: string;
  toggleHotelProposal: (hotel: Hotel) => void;
  toggleRoomProposal: (hotel: Hotel, room: RoomClass) => void;
  goToDetails: (params: { hotel: Hotel; room: RoomClass }) => void;
  onCardLocalAction: (action: {
    kind: 'open-preferred' | 'open-refund' | 'show-rooms';
    hotel: Hotel;
    room?: RoomClass;
    show?: boolean | null;
  }) => void;
}

const openRoomsPanel = (hotel: Hotel, handlers: HotelCardActionHandlers) => {
  handlers.onCardLocalAction({ kind: 'show-rooms', hotel, show: true });
  if (handlers.showRoomsInTab && handlers.displayStyle === '2') {
    handlers.setAreActiveRooms?.(true);
  } else if (handlers.showRoomsInTab) {
    handlers.setActiveTab(3);
  }
};

export const applyHotelCardAction = (
  payload: HotelCardActionPayload,
  hotels: Hotel[],
  handlers: HotelCardActionHandlers,
  activeHotel?: Hotel | null,
): boolean => {
  const hotel = findHotelForCardAction(hotels, payload, activeHotel, handlers.pinnedHotelsIds);
  if (!hotel) {
    return false;
  }

  const room = findRoomForCardAction(hotel, payload);
  const pinId = getOriginalHotelKey(hotel.htuKey);

  handlers.setActiveHotel(hotel);

  switch (payload.action) {
    case 'show-rooms': {
      handlers.onCardLocalAction({
        kind: 'show-rooms',
        hotel,
        show: payload.show,
      });
      return true;
    }
    case 'pin': {
      handlers.setPinnedHotelsIds((prev) => {
        const shouldPin =
          typeof payload.pinned === 'boolean' ? payload.pinned : !prev.includes(pinId);
        if (shouldPin) {
          return prev.includes(pinId) ? prev : [...prev, pinId];
        }
        return prev.filter((id) => id !== pinId);
      });
      return true;
    }
    case 'center-me': {
      handlers.setCentered(getCenteredHotel(hotel));
      handlers.setActiveTab(0);
      return true;
    }
    case 'www': {
      const query = `Hotel ${hotel.item.name}, ${hotel.addressInfo?.city || ''}`.trim();
      window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, '_blank');
      return true;
    }
    case 'focus': {
      const marker: FocusedMarker = {
        lat: hotel.position.latitude,
        lng: hotel.position.longitude,
        htuKey: hotel.htuKey,
        type: 'hotel',
        open: true,
        name: hotel.item.name || '',
      };
      handlers.setActiveTab(0);
      handlers.setFocusedHotel(marker);
      window.setTimeout(() => {
        handlers.setFocusedHotel({ ...marker, open: true });
      }, 120);
      return true;
    }
    case 'room-offers': {
      openRoomsPanel(hotel, handlers);
      return true;
    }
    case 'add-to-proposal': {
      openRoomsPanel(hotel, handlers);
      if (room && (payload.roomName || payload.roomKey)) {
        handlers.toggleRoomProposal(hotel, room);
      } else {
        handlers.toggleHotelProposal(hotel);
      }
      return true;
    }
    case 'open-preferred': {
      handlers.onCardLocalAction({ kind: 'open-preferred', hotel });
      return true;
    }
    case 'open-refund': {
      if (!room) {
        return false;
      }
      openRoomsPanel(hotel, handlers);
      handlers.onCardLocalAction({ kind: 'open-refund', hotel, room });
      return true;
    }
    case 'click-price': {
      if (!room) {
        return false;
      }
      handlers.setActiveHotel({ ...hotel, highlightedUk: room.rcUniqueKey });
      if (handlers.showRoomsInTab && handlers.displayStyle === '2') {
        handlers.setAreActiveRooms?.(true);
      } else if (handlers.showRoomsInTab) {
        handlers.setActiveTab(3);
      } else {
        handlers.goToDetails({ hotel, room });
      }
      return true;
    }
    case 'filter-rooms': {
      openRoomsPanel(hotel, handlers);
      publishLocalRoomFilterAction({
        hotelKey: pinId,
        roomFilterName: payload.roomFilterName,
        roomRefundableOnly: payload.roomRefundableOnly,
        roomAccessibleOnly: payload.roomAccessibleOnly,
        roomBoardingOnly: payload.roomBoardingOnly,
        roomMealPlans: payload.roomMealPlans,
        roomSortByCxl: payload.roomSortByCxl,
        clearRoomFilters: payload.clearRoomFilters,
      });
      return true;
    }
    default:
      return false;
  }
};
